import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { Util, XmStore, XmStoreUtil } from 'core/services';
import { InternationalRate } from 'store/international-rates/models/international-rates';
import { CountryStatus } from 'international/constants';

@Component({
    selector: 'xm-search',
    styleUrls: ['./search.scss'],
    templateUrl: './search.html',
    standalone: false
})
export class XmSearch implements AfterViewInit, OnInit, OnDestroy {
    @Input() public textBoxPlaceHolder: string;
    @Output() public countryDetails: EventEmitter<InternationalRate> = new EventEmitter<InternationalRate>();

    public autocompleteOptions: AutocompleteItem[] = [];
    public disabledSuggestions: AutocompleteItem[] = [];
    public query: string;

    @ViewChild('searchBox') private searchBox: ElementRef<HTMLInputElement>;

    private xmStore: XmStore;
    private subscriptions: Subscription[] = [];
    private internationalRates: InternationalRate[] = [];
    private popularCountries: InternationalRate[] = [];

    constructor(xmStore: XmStore) {
        Object.assign(this, { xmStore });
    }

    public ngOnInit(): void {
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peekMany<InternationalRate>(InternationalRate), (result: InternationalRate[]) => {
            this.internationalRates = result;
            if (result && result.length) {
                this.popularCountries = result.filter((country: InternationalRate) => country.status === CountryStatus.POPULAR);

                // To display popular countries when the form is pristine
                this.assignAutocompleteOptions(this.popularCountries);
                this.assignDefaultDisabledOptions();
            }
        }));
    }

    public ngAfterViewInit(): void {
        this.subscriptions.push(
            fromEvent(this.searchBox.nativeElement, 'input').pipe(
                debounceTime(1e2),
                distinctUntilChanged()
            ).subscribe(() => {
                const query: string = this.query?.replace(/\s+/g, '').trim().toLowerCase() || '';
                if (!query) {
                    this.assignAutocompleteOptions(this.popularCountries);
                    this.assignDefaultDisabledOptions();

                    return;
                }
                const filtered: InternationalRate[] = this.internationalRates.filter((rate: InternationalRate) => {
                    const displayName: string = rate.displayName.replace(/\s+/g, '').toLowerCase();

                    return this.findQueryInSearchTags(rate.searchTags) || displayName.includes(query);
                });

                this.assignAutocompleteOptions(filtered);
                this.disabledSuggestions = [{ label: `${filtered.length} Results` }];
            })
        );
    }

    public suggestionSelected(event: AutocompleteItem): void {
        this.setSelectedCountry(event.value || event.label);
    }

    public clearInputSearch(event: boolean): void {
        if (event) {
            this.assignAutocompleteOptions(this.popularCountries);
            this.assignDefaultDisabledOptions();
        }
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    private findQueryInSearchTags(tags: string[]): boolean {
        return !!tags.find((tag: string) => tag.toLowerCase().includes(this.query.toLowerCase()));
    }

    private setSelectedCountry(key: string = ''): void {
        const rate: InternationalRate = this.internationalRates.find((country: InternationalRate) => country.displayName.toLowerCase() === key.toLowerCase());
        if (!rate) {
            this.autocompleteOptions = [];
        }
        this.countryDetails.emit(rate);
    }

    private assignAutocompleteOptions(values: InternationalRate[]): void {
        this.autocompleteOptions = values.map((rate: InternationalRate) => {
            const item: AutocompleteItem = {
                label: rate.displayName
            };

            return item;
        });
    }

    private assignDefaultDisabledOptions(): void {
        this.disabledSuggestions = [{
            label: 'Popular Countries',
            styles: { 'text-transform': 'uppercase' }
        }];
    }
}
